<template>
  <div>
    <div
      v-if="hasRole('ROLE_SALES_MANAGER') || hasRole('ROLE_BUSINESS_ADMIN')"
      class="level is-mobile"
    >
      <div class="level-left" />
      <div class="level-right">
        <div class="level-item">
          <b-field class="is-light">
            <b-select
              v-model="selected"
              placeholder="FILTER BY"
              size="is-small"
            >
              <option value="all">
                All
              </option>

              <hr class="dropdown-divider">

              <option value="self">
                Self
              </option>

              <hr class="dropdown-divider">

              <option
                v-for="(subordinate, subordinateIndex) in subordinatesList"
                :key="subordinateIndex"
                :value="subordinate.id"
              >
                {{ subordinate.fullname.trim() }}
              </option>

              <hr class="dropdown-divider">

              <option
                v-for="team in teams"
                :key="team.id"
                :value="`team${team.id}`"
              >
                {{ team.name }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
    </div>

    <div class="card is-rounded-card is-spaced-top">
      <header class="card-header">
        <p class="card-header-title">
          top 10 accounts by expected revenue
        </p>
      </header>

      <div class="card-content">
        <highcharts
          v-if="hasData"
          :options="topTenAccountsByExpectedRevenueSeries"
        />

        <div
          v-else
          class="level is-mobile"
        >
          <div class="level-item has-text-centered">
            <div>
              <figure class="image">
                <img
                  src="@/assets/images/no-data.svg"
                  alt="No Data"
                >
              </figure>
              <b-button
                type="is-primary"
                size="is-small"
              >
                no data found
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Chart } from 'highcharts-vue';

export default {
  name: 'TopTenAccountsByExpectedRevenue',

  components: {
    highcharts: Chart,
  },

  data: () => ({
    self: localStorage.getItem('user_id'),
    selected: '',
  }),

  computed: {
    ...mapGetters({
      topTenAccountsByExpectedRevenue: 'Graphs/getTopTenAccountsByExpectedRevenue',
      subordinatesList: 'Users/getSubordinates',
      teams: 'Graphs/getTeams',
    }),

    hasData() {
      const data = { ...this.topTenAccountsByExpectedRevenue };
      const totalArray = Object.keys(data);
      return totalArray.length > 0;
    },

    topTenAccountsByExpectedRevenueSeries() {
      const data = { ...this.topTenAccountsByExpectedRevenue };
      const categories = Object.keys(data);
      // data = Object.values(data).sort((a, b) => b.totalrevenue - a.totalrevenue);
      let leadSeries = [];
      let opportunitySeries = [];
      if (categories.length > 0) {
        leadSeries = Object.values(data).map((el) => {
          const values = [];
          Object.entries(el).filter(([key, value]) => {
            if (key.startsWith('lead')) {
              values.push(value);
            }
            return '';
          });
          return values.reduce((a, b) => a + b, 0);
        });

        opportunitySeries = Object.values(data).map((el) => {
          const values = [];
          Object.entries(el).filter(([key, value]) => {
            if (key.startsWith('opportunity')) {
              values.push(value);
            }
            return '';
          });
          return values.reduce((a, b) => a + b, 0);
        });
      }
      const openDrilldown = (type) => {
        const selectedSubordinate = this.subordinatesList.find(
          (subordinate) => subordinate.id === this.selected,
        );
        if (selectedSubordinate) {
          this.$emit('open-drilldown', {
            name: type.name,
            filters: {
              accountName: type.accountname,
              owners: this.selected === 'self'
                ? localStorage.getItem('full_name') : selectedSubordinate.fullname,
            },
          });
        } else if (this.selected.toString().startsWith('team')) {
          this.$emit('open-drilldown', {
            name: type.name,
            filters: {
              accountName: type.accountname,
              teamId: this.selected.toString().substring(4),
            },
          });
        } else {
          this.$emit('open-drilldown', {
            name: type.name,
            filters: { accountName: type.accountname },
          });
        }
      };

      return {
        chart: {
          type: 'bar',
          style: {
            fontFamily: 'Open Sans',
          },
        },

        title: {
          text: null,
        },

        credits: {
          enabled: false,
        },

        colors: ['#3B90D3', '#1E3D73'],

        xAxis: {
          categories,
        },

        yAxis: {
          allowDecimals: false,
        },

        tooltip: {
          pointFormat: '{series.name}: <b>$ {point.y:,.2f}</b> <br>',
          shared: true,
        },

        plotOptions: {
          series: {

            allowPointSelect: true,
            point: {
              events: {
                select(event) {
                  if (event.target.color === '#3B90D3') {
                    openDrilldown({ name: 'lead', accountname: event.target.category });
                  } else {
                    openDrilldown({ name: 'opportunity', accountname: event.target.category });
                  }
                },
              },
            },
          },
        },

        series: [
          {
            name: 'Leads',
            data: leadSeries,
          },
          {
            name: 'Opportunities',
            data: opportunitySeries,
          },
        ],

        exporting: {
          buttons: {
            contextButton: {
              menuItems: [
                'printChart',
                'separator',
                'downloadPNG',
                'downloadJPEG',
                'downloadPDF',
                'downloadSVG',
              ],
            },
          },
        },
      };
    },
  },

  watch: {
    selected: {
      handler(value) {
        if (value === 'all') {
          this.$store.dispatch('Graphs/fetchTopTenAccountsByExpectedRevenue', { teamid: '', userid: '' });
        } else if (value === 'self') {
          this.$store.dispatch('Graphs/fetchTopTenAccountsByExpectedRevenue', { teamid: '', userid: this.self });
        } else if (value.toString().startsWith('team')) {
          this.$store.dispatch('Graphs/fetchTopTenAccountsByExpectedRevenue', { teamid: value.toString().substring(4), userid: '' });
        } else {
          this.$store.dispatch('Graphs/fetchTopTenAccountsByExpectedRevenue', { teamid: '', userid: value });
        }
      },

      deep: true,
    },
  },

  async created() {
    try {
      if (this.hasRole('ROLE_SALES_PERSON')) {
        await this.$store.dispatch('Graphs/fetchTopTenAccountsByExpectedRevenue', { teamid: '', userid: this.self });
      } else {
        await this.$store.dispatch('Graphs/fetchTopTenAccountsByExpectedRevenue', { teamid: '', userid: '' });
      }
      this.selected = this.hasRole('ROLE_SALES_PERSON') ? 'self' : 'all';
    } catch (error) {
      console.error(error);
    }
  },

};
</script>

<style>

</style>
